import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LOG_SERVICE_FE_CONFIG } from './log-service-fe.config';

export interface LogRecord {
  id: number;
  service_name: string;
  level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';
  message: string;
  trace_id: string | null;
  component: string | null;
  metadata: Record<string, unknown>;
  timestamp: string;
}

export type FilterMatchMode =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith';

export interface GetLogsParams {
  limit?: number;
  offset?: number;
  level?: string;
  levelMode?: FilterMatchMode;
  service?: string;
  serviceMode?: FilterMatchMode;
  from?: string;
  to?: string;
  search?: string;
  searchMode?: FilterMatchMode;
  component?: string;
  componentMode?: FilterMatchMode;
  traceId?: string;
  traceIdMode?: FilterMatchMode;
  sort?: 'timestamp' | 'level' | 'service_name' | 'component';
  order?: 'asc' | 'desc';
}

export interface GetLogsResponse {
  logs: LogRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface DeleteLogResponse {
  deleted: boolean;
  id: number;
}

@Injectable({
  providedIn: 'root',
})
export class LogAdminService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(LOG_SERVICE_FE_CONFIG, { optional: true });

  getLogs(params: GetLogsParams, accessToken: string) {
    let httpParams = new HttpParams();
    if (params.limit != null)
      httpParams = httpParams.set('limit', params.limit);
    if (params.offset != null)
      httpParams = httpParams.set('offset', params.offset);
    if (params.level) httpParams = httpParams.set('level', params.level);
    if (params.levelMode)
      httpParams = httpParams.set('level_mode', params.levelMode);
    if (params.service) httpParams = httpParams.set('service', params.service);
    if (params.serviceMode)
      httpParams = httpParams.set('service_mode', params.serviceMode);
    if (params.from) httpParams = httpParams.set('from', params.from);
    if (params.to) httpParams = httpParams.set('to', params.to);
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.searchMode)
      httpParams = httpParams.set('search_mode', params.searchMode);
    if (params.component)
      httpParams = httpParams.set('component', params.component);
    if (params.componentMode)
      httpParams = httpParams.set('component_mode', params.componentMode);
    if (params.traceId) httpParams = httpParams.set('trace_id', params.traceId);
    if (params.traceIdMode)
      httpParams = httpParams.set('trace_id_mode', params.traceIdMode);
    if (params.sort) httpParams = httpParams.set('sort', params.sort);
    if (params.order) httpParams = httpParams.set('order', params.order);

    return this.http.get<GetLogsResponse>(`${this.getEndpoint()}/logs`, {
      headers: this.authHeaders(accessToken),
      params: httpParams,
    });
  }

  deleteLog(id: number, accessToken: string) {
    return this.http.delete<DeleteLogResponse>(
      `${this.getEndpoint()}/logs/${id}`,
      {
        headers: this.authHeaders(accessToken),
      },
    );
  }

  private getEndpoint(): string {
    return this.config?.adminEndpoint ?? '/log-api';
  }

  private authHeaders(accessToken: string): HttpHeaders {
    return new HttpHeaders({ authorization: `Bearer ${accessToken}` });
  }
}
